/**
 * cache/persister.ts — TanStack persistQueryClient contract pins.
 *
 * Spec anchors:
 *   - T2227 / h3-L1937 — Persistence · what survives JSON, what doesn't
 *   - T2228 / h3-L1945 — Bottom line (cache section close)
 *
 * THIS FILE IS A CONTRACT MODULE. It exports:
 *   - `PERSISTENCE_INVARIANTS` — frozen pin set, asserted by tests.
 *   - `PersistenceSchemaMismatchError` — surface for failed hydration.
 *   - `assertJsonRoundTripSafe(value)` — dev-mode invariant check used
 *     by the `setQueryData` runtime assertion (T2227 acceptance #2).
 *   - `currentSchemaByte()` — single-byte version stamp embedded in the
 *     persisted blob so a version bump cold-starts the cache rather
 *     than partially hydrating mismatched shapes.
 *
 * It does NOT instantiate the persister itself — that wiring happens at
 * the SDK provider site (`src/provider/*`) so the QueryClient can be
 * scoped per-test. This module is the load-bearing CONTRACT that the
 * provider must compose with.
 *
 * The default `createAsyncStoragePersister` uses `JSON.stringify`.
 * JSON-safe values round-trip cleanly; **`Set` / `Map` become `{}`,
 * `BigInt` throws, `Date` silently coerces to a string, `undefined` /
 * `NaN` / `Infinity` are lost**.
 *
 * Two safeguards apply, BOTH enforced:
 *
 *   1. Type-level: every spec input is `satisfies JsonSerialisableArgs`
 *      (see `../types/JsonSerialisableArgs.ts`). Compile fails on
 *      `Date | Map | Set | BigInt | class instance` in spec input shape.
 *   2. Runtime (dev only): `setQueryData` calls `assertJsonRoundTripSafe`
 *      on cached state. Throws synchronously on `Date | Map | Set |
 *      class instance` in the payload — surfaces a developer-facing
 *      `PERSISTENCE_SCHEMA_MISMATCH` SdkError category.
 *
 * NO superjson at the persister layer. Earlier drafts hedged with
 * superjson and silently masked backend-payload regressions — dropped.
 */

// ===========================================================================
// T2227 · h3-L1937 — Persistence · what survives JSON, what doesn't
// ===========================================================================

/**
 * Pinned persistence invariants from L1937.
 *
 * Each entry maps a concrete failure mode to its closing safeguard. A
 * test pinned to L1937 asserts every value here; reverting either
 * safeguard at the provider layer is caught by hydration tests.
 */
export const PERSISTENCE_INVARIANTS_L1937 = Object.freeze({
  serializer: 'JSON.stringify' as const,
  noSuperjson: true,
  /** D6/D9 amounts cached as strings — JSON-safe by construction. */
  amountsAsStrings: true,
  /** Timestamps cached as ISO strings — never Date instances. */
  timestampsAsIsoStrings: true,
  /** Dev-mode assertion throws on `Date | Map | Set | class instance`. */
  devRuntimeAssertion: true,
  /** Provider component MUST be inside `QueryClientProvider`. */
  mountedInsideQueryClientProvider: true,
  /** Bump `SCHEMA_BYTE` to cold-start the cache on shape changes. */
  versionStampedHydration: true,
} as const);

/**
 * Schema byte. Persister embeds this in the buster string. Bump on any
 * cache-payload shape change (new field, removed field, changed
 * encoding) to force cold-start rather than partial-load with stale
 * shape.
 *
 * Read by the provider via `currentSchemaByte()` so a single
 * source-of-truth controls both the persister buster and the
 * mismatch-error path.
 */
const SCHEMA_BYTE = 2;

export function currentSchemaByte(): number {
  return SCHEMA_BYTE;
}

/**
 * Persister rejects hydration with this SdkError category when the
 * persisted blob's schema byte doesn't match `currentSchemaByte()`.
 * Provider catches it, clears the persisted blob, and starts cold.
 *
 * Distinct from a JSON-parse error (which is a corruption) so telemetry
 * can distinguish "we bumped the schema on purpose" from "the user's
 * storage is corrupted".
 */
export class PersistenceSchemaMismatchError extends Error {
  readonly category = 'PERSISTENCE_SCHEMA_MISMATCH' as const;
  readonly expected: number;
  readonly found: number;

  constructor(expected: number, found: number) {
    super(`Persistence schema mismatch: expected ${expected}, found ${found}`);
    this.name = 'PersistenceSchemaMismatchError';
    this.expected = expected;
    this.found = found;
  }
}

/**
 * Dev-mode runtime assertion. Throws on any value that JSON cannot
 * round-trip cleanly:
 *
 *   - `Date`            → silently coerced to ISO string (loses type)
 *   - `Map` / `Set`     → become `{}` (loses every entry)
 *   - `BigInt`          → throws in `JSON.stringify`
 *   - class instances   → become plain `{}` (loses methods/prototype)
 *   - `undefined`       → dropped from objects, becomes `null` in arrays
 *   - `NaN` / `Infinity`→ become `null`
 *
 * Recursive over plain objects and arrays. Bounded recursion via a
 * `WeakSet` cycle guard so a circular payload throws cleanly instead of
 * hanging.
 *
 * Called by the provider's `setQueryData` wrapper in dev builds. In
 * production this assertion is dead-code-eliminated (provider passes
 * `false` for `enabled`).
 */
export function assertJsonRoundTripSafe(
  value: unknown,
  path: string = '$',
  seen: WeakSet<object> = new WeakSet(),
): void {
  if (value === undefined) {
    throw new PersistenceShapeViolationError(path, 'undefined');
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    throw new PersistenceShapeViolationError(path, 'NaN-or-Infinity');
  }
  if (typeof value === 'bigint') {
    throw new PersistenceShapeViolationError(path, 'bigint');
  }
  if (value === null) return;
  if (typeof value !== 'object') return; // primitive — fine

  if (seen.has(value)) {
    throw new PersistenceShapeViolationError(path, 'cycle');
  }
  seen.add(value);

  if (value instanceof Date) {
    throw new PersistenceShapeViolationError(path, 'Date');
  }
  if (value instanceof Map) {
    throw new PersistenceShapeViolationError(path, 'Map');
  }
  if (value instanceof Set) {
    throw new PersistenceShapeViolationError(path, 'Set');
  }

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      assertJsonRoundTripSafe(value[i], `${path}[${i}]`, seen);
    }
    return;
  }

  // Plain object — must have Object.prototype or null prototype. Class
  // instances have a different prototype and lose methods on JSON
  // round-trip.
  const proto = Object.getPrototypeOf(value);
  if (proto !== null && proto !== Object.prototype) {
    throw new PersistenceShapeViolationError(path, 'class-instance');
  }
  for (const [k, v] of Object.entries(value)) {
    assertJsonRoundTripSafe(v, `${path}.${k}`, seen);
  }
}

export class PersistenceShapeViolationError extends Error {
  readonly category = 'PERSISTENCE_SCHEMA_MISMATCH' as const;
  readonly path: string;
  readonly kind: string;

  constructor(path: string, kind: string) {
    super(`Non-JSON-safe value at ${path}: ${kind}`);
    this.name = 'PersistenceShapeViolationError';
    this.path = path;
    this.kind = kind;
  }
}

// ===========================================================================
// T1001 · createWebPersister — web persister bound to platform sync storage.
// ===========================================================================

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { isMarketConfigCatalog } from '../../core/internal/market-config-catalog.js';
import { isRealtimeQueryKey } from './realtime-query-registry.js';
import { isViewerSensitiveQuery } from './viewer-sensitive-query-registry.js';

/**
 * `Persister` is the structural shape TanStack's persistQueryClient expects.
 * The type is published from `@tanstack/query-persist-client-core` which is
 * an internal package — we derive it from `createSyncStoragePersister`'s
 * return type to avoid the deep-import.
 */
type Persister = ReturnType<typeof createSyncStoragePersister>;

/**
 * Storage shape the platform adapter MUST expose for the web persister.
 * Mirrors the `Storage` shape TanStack expects — adapter-side is backed
 * by `localStorage`.
 */
export interface SyncStorageLike {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
  readonly removeItem: (key: string) => void;
}

/**
 * Platform adapter slice consumed by `createWebPersister`. The full
 * `PlatformAdapter` (T1102) will expose more — this is the minimal
 * surface the persister needs.
 */
export interface PersisterPlatformAdapter {
  readonly storage: { readonly sync: SyncStorageLike };
}

/**
 * Options for `createWebPersister`. NO `serialize` / `deserialize`
 * overrides are exposed — the SDK is JSON-only by spec invariant
 * (L1937). Passing a custom serializer is a TS error at the call site.
 */
export interface SdkPersisterOptions {
  /** Storage key. Defaults to `'morfi-sdk-cache-v2'`. */
  readonly key?: string;
  /** Throttle in ms. Matches TanStack default (1000 ms). */
  readonly throttleTime?: number;
  /** Platform adapter exposing the sync storage. */
  readonly platform: PersisterPlatformAdapter;
}

/** Default throttle — TanStack's createSyncStoragePersister source default. */
export const DEFAULT_PERSISTER_THROTTLE_MS = 1000 as const;

/** Default storage key. Schema-byte-stamped consumers append the version. */
export const DEFAULT_PERSISTER_KEY = 'morfi-sdk-cache-v2' as const;

/**
 * Build the web persister. JSON serializer ONLY — file 05 §1586 forbids
 * superjson. Cache values are JSON-safe by construction; the
 * `JsonSerialisableArgs` type-level check (T106) plus the dev-mode
 * `setQueryData` runtime guard (`assertJsonRoundTripSafe`) enforce the
 * invariant from both ends.
 *
 * The 1000 ms throttle is intentionally retained — pagehide/freeze
 * synchronous flush (T1004) captures writes lost in the throttle window.
 */
export function createWebPersister(opts: SdkPersisterOptions): Persister {
  const persister = createSyncStoragePersister({
    storage: opts.platform.storage.sync,
    key: opts.key ?? DEFAULT_PERSISTER_KEY,
    throttleTime: opts.throttleTime ?? DEFAULT_PERSISTER_THROTTLE_MS,
    // NOTE: no `serialize` / `deserialize` overrides — TanStack defaults
    // to JSON.stringify / JSON.parse.
  });
  return {
    persistClient: (client) =>
      persister.persistClient(excludeNonpersistentQueriesFromPersistedClient(client)),
    restoreClient: async () => {
      const restored = await persister.restoreClient();
      return restored === undefined
        ? undefined
        : excludeNonpersistentQueriesFromPersistedClient(restored);
    },
    removeClient: () => persister.removeClient(),
  };
}

// ===========================================================================
// T511 · createSdkPersister · platform-agnostic persister wiring
// ===========================================================================

/**
 * Storage shim accepted by `createSdkPersister`. Mirrors the minimal API
 * of `idb-keyval` (web sync), `@react-native-async-storage/async-storage`
 * (RN async), and MMKV. Returns `string | null` from `getItem` and a
 * `string | Promise<void>` from `setItem` / `removeItem`.
 */
export interface SdkPersisterStorageShim {
  readonly getItem: (key: string) => Promise<string | null> | string | null;
  readonly setItem: (key: string, value: string) => Promise<void> | void;
  readonly removeItem: (key: string) => Promise<void> | void;
}

/**
 * Lean options for `createSdkPersister` — used by the SDK provider when
 * the caller doesn't have a full `PlatformAdapter` instance in hand
 * (e.g. tests, codegen golden fixtures).
 *
 * The persister this returns is JSON-only — passing a custom serializer
 * is structurally impossible because the surface has no
 * `serialize`/`deserialize` slots. File 05 §1586 invariant.
 */
export interface CreateSdkPersisterOptions {
  readonly storage: SdkPersisterStorageShim;
  /** Default 100 ms — matches T7106 broadcast queue cadence. */
  readonly throttleMs?: number;
  readonly key?: string;
}

/** Default throttle for `createSdkPersister` — 100 ms (T511). */
export const SDK_PERSISTER_DEFAULT_THROTTLE_MS = 100 as const;

/**
 * Build a `Persister` from a minimal storage shim. JSON serializer only.
 *
 * The shim adapts both sync (web idb-keyval) and async (RN MMKV /
 * SecureStore) storages — the underlying TanStack
 * `createAsyncStoragePersister` already awaits both.
 */
export function createSdkPersister(opts: CreateSdkPersisterOptions): Persister {
  const key = opts.key ?? DEFAULT_PERSISTER_KEY;
  return {
    persistClient: async (client: unknown) => {
      // BigInt-safe stringify — a cache row may carry a bigint (a realtime
      // `set` of an int64 payload field); a bare `JSON.stringify` would throw
      // and silently break persistence. Cache row fields are string-typed, so
      // a bigint coerced to its base-10 string round-trips correctly on
      // restore. See `.claude` memory `feedback-morfi-json-bigint`.
      const out = opts.storage.setItem(
        key,
        JSON.stringify(excludeNonpersistentQueriesFromPersistedClient(client), (_k, v) =>
          typeof v === 'bigint' ? v.toString() : v,
        ),
      );
      if (out instanceof Promise) await out;
    },
    restoreClient: async () => {
      const raw = await opts.storage.getItem(key);
      if (raw === null || raw === undefined) return undefined;
      const parsed: unknown = JSON.parse(raw);
      return excludeNonpersistentQueriesFromPersistedClient(parsed) as ReturnType<
        Persister['restoreClient']
      > extends Promise<infer R>
        ? R
        : never;
    },
    removeClient: async () => {
      const out = opts.storage.removeItem(key);
      if (out instanceof Promise) await out;
    },
  } as unknown as Persister;
}

/**
 * Filter for the persist flush — excludes NATS-driven realtime queries.
 * Realtime queries (e.g. the markets feed) carry `staleTime: Infinity` and
 * re-hydrate from the live wire on resume, so persisting them only restores
 * a stale snapshot across reloads; POLL/STATIC queries persist normally.
 *
 * Two signals, because `meta` alone is insufficient: an SSR-prefetched query
 * reaches the flush with NO meta (the server builds it via `setQueryData`,
 * `dehydrate` omits meta, and the client only re-applies meta once a hook
 * observes the query). So we also consult the queryKey against the realtime
 * prefix registry, which the hook factories populate at module load. See
 * [`realtime-query-registry.ts`](./realtime-query-registry.ts).
 */
export function shouldDehydrateQuery(query: {
  queryKey?: readonly unknown[] | undefined;
  meta?:
    | {
        realtime?: boolean;
        persist?: boolean;
        viewerSensitive?: boolean;
        specName?: string;
      }
    | undefined;
}): boolean {
  if (isViewerSensitiveQuery(query)) return false;
  if (query.meta?.realtime === true) return false;
  if (Array.isArray(query.queryKey)) {
    if (query.queryKey[0] === 'me' || query.queryKey[1] === 'viewer') return false;
    if (isRealtimeQueryKey(query.queryKey)) return false;
  }
  return true;
}

/**
 * Apply the same persistence policy to both new writes and old snapshots.
 * Filtering again during restore self-heals blobs written before a resource
 * was classified as realtime, without invalidating unrelated cached data or
 * paused mutations.
 */
export function excludeNonpersistentQueriesFromPersistedClient<T>(client: T): T {
  if (!isRecord(client) || !isRecord(client.clientState)) return client;
  const queries = client.clientState.queries;
  if (!Array.isArray(queries)) return client;
  const eligibleQueries = queries.filter((query) => {
    if (!isRecord(query)) return false;
    if (
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === 'market-configs' &&
      (!isRecord(query.state) || !isMarketConfigCatalog(query.state.data))
    )
      return false;
    return shouldDehydrateQuery(query);
  });
  if (eligibleQueries.length === queries.length) return client;
  return {
    ...client,
    clientState: { ...client.clientState, queries: eligibleQueries },
  } as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Restore-time gate replicating TanStack `persistQueryClientRestore`'s
 * `maxAge` + `buster` semantics (that helper is not a dependency here).
 * Returns false — meaning "drop the blob and cold-start" — when the blob
 * has no timestamp, is older than `maxAge`, or was written under a
 * different schema `buster`.
 */
export function isPersistedClientUsable(
  persisted: { timestamp?: number; buster?: string },
  opts: { maxAge: number; buster: string; now: number },
): boolean {
  if (typeof persisted.timestamp !== 'number') return false;
  if (opts.now - persisted.timestamp > opts.maxAge) return false;
  if (persisted.buster !== opts.buster) return false;
  return true;
}
